<script setup lang="ts">
import AddressFieldsGrid from "@/components/common/Address/AddressFieldsGrid.vue";
import { useAppStore } from "@/store/app";
import type { GroupField } from "@/util/methods";
import { BILLING_ADDRESS_FIELD_KEY_BY_TAG } from "@/util/sellProductAddress";
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";

type BillingTag = keyof typeof BILLING_ADDRESS_FIELD_KEY_BY_TAG;

interface BillingPreviewField extends Partial<GroupField> {
  tag: BillingTag;
  label: string;
}

const PROJECTED_SHIPPING_TAGS: BillingTag[] = [
  "address",
  "city",
  "state",
  "country",
  "postal_code",
];

const props = defineProps({
  inputClass: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const store = useAppStore();
const { t } = useI18n();

const asBillingField = (
  field: Partial<GroupField> & { label: string },
  tag: BillingTag
): BillingPreviewField => ({
  ...field,
  tag,
  fieldKey: BILLING_ADDRESS_FIELD_KEY_BY_TAG[tag],
  hiddenFieldQueryKey: BILLING_ADDRESS_FIELD_KEY_BY_TAG[tag],
});

const fixedFields = computed<BillingPreviewField[]>(() => {
  const fullName = t("fields.personal.fullName");
  const phoneNumber = t("common.sellProductsAddress.phoneNumber");
  return [
    asBillingField(
      { label: fullName, placeholder: fullName, required: true },
      "full_name"
    ),
    asBillingField(
      { label: phoneNumber, placeholder: phoneNumber, required: true },
      "phone"
    ),
  ];
});

const projectedShippingFields = computed<BillingPreviewField[]>(() => {
  const children = store.addressSettings?.children ?? [];
  return PROJECTED_SHIPPING_TAGS.flatMap((tag) => {
    const source = children.find((field) => field.tag === tag);
    return source ? [asBillingField(source, tag)] : [];
  });
});

const streetField = computed(() =>
  projectedShippingFields.value.find((field) => field.tag === "address")
);

const fullWidthFields = computed(() => [
  ...fixedFields.value,
  ...(streetField.value ? [streetField.value] : []),
]);

const pairedFields = computed(() =>
  projectedShippingFields.value.filter((field) => field.tag !== "address")
);

const showLabels = computed(() => store.builderConfig.formLabelVisible ?? true);
const inputClasses = computed(() => [
  ...props.inputClass,
  ...(store.builderConfig.inputStyleType === "line" ? ["line-form-style"] : []),
]);
</script>

<template>
  <section class="flex flex-col self-stretch" aria-labelledby="billing-address-preview-title">
    <h3 id="billing-address-preview-title" class="mb-3 text-base font-semibold text-gray-900">
      {{ t("common.sellProductsAddress.billingAddress") }}
    </h3>

    <AddressFieldsGrid
      :full-width-fields="fullWidthFields"
      :paired-fields="pairedFields"
      :field-spacing="store.formStyle.fieldSpacing"
    >
      <template #field="{ field }">
        <div class="flex w-full flex-col items-start gap-1 self-stretch">
          <div v-if="showLabels || field.hidden" class="flex items-start">
            <label
              v-if="showLabels"
              class="font-inter break-words text-sm font-medium text-gray-900"
              :for="`billing-${field.tag}`"
            >
              {{ field.label }}<span v-if="field.required" class="ml-1 text-error-500">*</span>
            </label>
            <small
              v-if="field.hidden"
              class="ml-[5px] rounded-sm bg-[#e1e1e1] px-[5px] py-[2px]"
            >
              {{ t("common.hidden") }}
            </small>
          </div>
          <input
            :id="`billing-${field.tag}`"
            :placeholder="field.placeholder"
            :aria-label="!showLabels ? field.label : undefined"
            :aria-required="field.required ? 'true' : undefined"
            class="form-placeholder form-control placeholder-input"
            :class="inputClasses"
            :style="{ fontSize: `${store.fieldStyle.placeholderFontSize ?? 14}px` }"
            readonly
          />
          <span v-if="field.shortLabel" class="font-inter break-words text-sm text-gray-600">
            {{ field.shortLabel }}
          </span>
        </div>
      </template>
    </AddressFieldsGrid>
  </section>
</template>

<style scoped>
.placeholder-input {
  padding: 8px 20px;
  border: 1px solid #acacacff;
  color: #8c8c8c;
}
</style>
